import { Link } from "react-router-dom";
import { CallToAction } from "@themes/pollinator/ui/CallToAction";
import { Button } from "@themes/pollinator/ui/Button";
import { Type } from "@themes/pollinator/ui/Type";
import { MarketingSection } from "../../components/Section";
import { InviteCodeFormContainer } from "@platform/features/invitation";
import { MarketingAudienceCardsContainer } from "../AudienceCtas";
import { INVITATION_TYPE, type Invitation } from "@core/features/invitation";
import type { MarketingSiteMode } from "@core/features/marketing";

export type Props = {
  onDashboardClick?: () => void;
  /** Whether enrollment is open; undefined while that is still being asked. */
  mode?: MarketingSiteMode;
  isAuthenticated: boolean;
  invitation?: Invitation | null;
  onJoinClick?: () => void;
  isEnrolled?: boolean;
  onClaimClick?: () => void;
  isClaimLoading?: boolean;
  invitationError?: string | null;
  errorInvitation?: Invitation | null;
  /** Shortcode from the URL, used in the fallback error title */
  shortcode?: string;
  onGoBackClick?: () => void;
  /** The participant network field, between the claim and the ways in */
  networkField?: React.ReactNode;
};

export function MarketingHero({
  onDashboardClick,
  mode,
  isAuthenticated,
  invitation,
  onJoinClick,
  isEnrolled,
  onClaimClick,
  isClaimLoading,
  invitationError,
  errorInvitation,
  shortcode,
  onGoBackClick,
  networkField,
}: Props) {
  const isInviteOnly = mode === "invite_only";
  const hasInvitation = !!invitation;
  const isOrgAdminInvitation =
    invitation?.type === INVITATION_TYPE.ORG_ADMIN ||
    invitation?.type === INVITATION_TYPE.ORG_ADMIN_ONBOARDER;
  const isErrorOrgAdmin =
    errorInvitation?.type === INVITATION_TYPE.ORG_ADMIN ||
    errorInvitation?.type === INVITATION_TYPE.ORG_ADMIN_ONBOARDER;

  return (
    <MarketingSection id="hero" className="pt-8 lg:pt-8">
      {/* 24px between the claim and its sentence on a phone, 16px on a desktop;
          the claim is inset further on a phone so it breaks after "Fund", and
          the design trims its line box to the letters there, hence the
          negative margin. The
          sentence gets a little more than the design's 908px measure because
          the web font sets it a touch wider, and it must stay on two lines. */}
      <div className="flex flex-col items-center text-center gap-6 md:gap-4 w-full px-8 lg:px-0">
        <Type
          as="h1"
          variant="h1"
          className="[font-feature-settings:normal] max-w-[1184px] leading-[45px] -my-[9px] tracking-[-0.85px] md:text-4xl lg:my-0 lg:text-[56px] md:leading-[45px] lg:leading-[76px] md:tracking-[-0.85px] lg:tracking-[-1.12px]"
        >
          Find your peers. Fund your work.
        </Type>
        <Type
          as="p"
          variant="body1"
          className="max-w-[960px] md:max-w-[512px] lg:max-w-[908px] text-[16px] leading-[24px] md:text-[16px] lg:text-[36px] md:leading-[24px] lg:leading-[49px] lg:tracking-[-0.72px]"
        >
          Join our network of artists, changemakers, organizations, and funders
          building collective resilience, together.
        </Type>
      </div>

      {/* The design puts 48px above the field and 64px below it on a desktop,
          24px and 32px on a phone, 32px and 48px on a tablet; the section's
          own gap supplies the rest. */}
      {networkField && (
        <div className="w-full max-w-[1184px] mx-auto mb-2 md:mt-2 md:mb-6 lg:mt-4 lg:mb-8 empty:hidden">
          {networkField}
        </div>
      )}

      {/* Invitation CTA: Show when a valid invitation is present */}
      {hasInvitation && (
        <CallToAction
          title={
            <>
              {isOrgAdminInvitation ? (
                <>
                  You have been invited as an Admin for{" "}
                  <span className="text-brand-100">
                    {invitation.owner_display}
                  </span>{" "}
                  in {invitation.program_name}.
                </>
              ) : (
                <>
                  <span className="text-brand-100">
                    {invitation.owner_display}
                  </span>{" "}
                  invited you to participate in{" "}
                  <span className="whitespace-nowrap">
                    {invitation.program_name}.
                  </span>
                </>
              )}
            </>
          }
          variant="platform"
          titleClassName="max-w-none"
          description={
            !isAuthenticated
              ? "Join by creating a new account or log in if you already have an account on Pollinator."
              : undefined
          }
        >
          {isAuthenticated && isEnrolled ? (
            <Button
              theme="dark"
              className="w-full lg:w-fit min-w-24"
              onClick={onDashboardClick}
              data-testid="invitation-dashboard-button"
            >
              Go to Dashboard
            </Button>
          ) : isAuthenticated ? (
            <Button
              theme="dark"
              className="w-full lg:w-fit min-w-24"
              onClick={onClaimClick}
              disabled={isClaimLoading}
              data-testid="invitation-claim-button"
            >
              {isClaimLoading ? "Joining…" : "Join"}
            </Button>
          ) : (
            <div className="flex gap-3">
              <Button
                theme="dark"
                className="w-full lg:w-fit min-w-24"
                onClick={onJoinClick}
                data-testid="invitation-join-button"
              >
                Join
              </Button>
              <Button
                as={Link}
                to={`/login?redirect=/invitation/${invitation.shortcode}/join`}
                theme="dark"
                variant="secondary"
                className="w-full lg:w-fit min-w-24"
                data-testid="invitation-login-button"
              >
                Log In
              </Button>
            </div>
          )}
        </CallToAction>
      )}

      {/* Invitation error: Show when invitation validation failed */}
      {!hasInvitation && invitationError && (
        <CallToAction
          title={
            errorInvitation ? (
              <>
                {isErrorOrgAdmin ? (
                  <>
                    You have been invited as an Admin for{" "}
                    <span className="text-brand-100">
                      {errorInvitation.owner_display}
                    </span>{" "}
                    in {errorInvitation.program_name}.
                  </>
                ) : (
                  <>
                    <span className="text-brand-100">
                      {errorInvitation.owner_display}
                    </span>{" "}
                    invited you to participate in{" "}
                    <span className="whitespace-nowrap">
                      {errorInvitation.program_name}.
                    </span>
                  </>
                )}
              </>
            ) : (
              `Invitation ${shortcode ?? ""} is no longer available.`.trim()
            )
          }
          variant="platform"
          titleClassName="max-w-none"
          onPrimaryClick={onGoBackClick}
          primaryButtonText="Go Back"
        >
          {invitationError !== "Request failed" && (
            <Type variant="body1" className="text-error">
              {invitationError}
            </Type>
          )}
        </CallToAction>
      )}

      {/* Authenticated + no invitation: Show "Go to Dashboard" CTA */}
      {!hasInvitation && isAuthenticated && !invitationError && (
        <CallToAction
          title="Welcome back!"
          variant="platform"
          onPrimaryClick={onDashboardClick}
          primaryButtonText="Go to Dashboard"
        />
      )}

      {/* Not authenticated + invite_only + no invitation + no error: Show signup CTA with inline code form */}
      {!hasInvitation &&
        !isAuthenticated &&
        !invitationError &&
        isInviteOnly && (
          <CallToAction
            title="Enrollment is now open for our May cycle!"
            description="Artists, founders, academics, changemakers, and leaders from organizations in our network* are invited to join us for five sessions of focused cross-pollination."
            variant="platform"
            className="rounded-lg"
          >
            <InviteCodeFormContainer />
          </CallToAction>
        )}

      {/* Closed mode shows no banner. A returning participant is not offered a
          way back in here at all: they are told to watch their inbox, in the
          words beside the waitlist form. */}
      {/* The ways in. Offered whatever the enrollment mode, because an invite
          code serves people who already have one and these are for everybody
          else: a funder, an organization, or someone asking to be considered. */}
      {!hasInvitation && !isAuthenticated && !invitationError && (
        <MarketingAudienceCardsContainer />
      )}
    </MarketingSection>
  );
}

export default MarketingHero;
